import Bounds from '../utils/Bounds';

// TODO: use ifdef
const debug = false;
let canvases;
if (debug) {
	canvases = {};
}

class DoublyNode  {
  object: any;
  previous: DoublyNode | null;
  next: DoublyNode | null;

  constructor (object: any, previous: DoublyNode | null, next: DoublyNode | null) {
    this.object = object;
    this.previous = previous;
    this.next = next;
  }
}

class DoublyList {
  last: DoublyNode | null;
  first: DoublyNode | null;

  constructor () {
    this.last = null;
    this.first = null;
  }

  add (object: any) {
    const newNode = new DoublyNode(object, null, this.first);

    if (this.first === null) {
      this.first = newNode;
      this.last = newNode;
    } else {
      // insertion before the first node
      this.first.previous = newNode;
      this.first = newNode;
    }

    return newNode;
  }

  remove (node) {
    // Removing any reference to the node
    if (node.next === null) {
      this.last = node.previous;
    } else {
      node.next.previous = node.previous;
    }

    if (node.previous === null) {
      this.first = node.next;
    } else {
      node.previous.next = node.next;
    }

    // Removing any reference from the node to any other element of the list
    node.previous = null;
    node.next = null;
  }
}

class ElementBounds extends Bounds {
  element: any;

  constructor (left: number, right: number, top: number, bottom: number) {
    super(left, right, top, bottom);
    this.element = null;
  }
}

let boxPartitioningID = 0;
export default class BoxPartitioning {
  private _id: number;
  private _freeSpace: DoublyList;
  occupiedBounds: Bounds;
  priorityZone: Bounds;

  constructor (left: number, right: number, top: number, bottom: number) {
		this._id = boxPartitioningID++;

    // TODO: do following optimizaitons?
    // - order by area for faster insertion?
    // - keep track of overlapping boxes of each free space?
    this._freeSpace = new DoublyList();

    const spaceBox = new ElementBounds(left, right, top, bottom);
    spaceBox.element = this._freeSpace.add(spaceBox);

    this.occupiedBounds = new Bounds(Infinity, -Infinity, Infinity, -Infinity);

    // defines where boxes should be placed in priority
    this.priorityZone = new Bounds(Infinity, -Infinity, Infinity, -Infinity);
    
		if (debug) {
			const canvas = document.createElement('canvas');
      const initCanvasDim = 200;
			canvas.style.position = 'absolute';
			canvas.style.top = '0px';
			canvas.style.left = '0px';
			canvas.style.width = (initCanvasDim / 2) + 'px';
			canvas.style.height = (initCanvasDim / 2) + 'px';
			canvas.width = initCanvasDim;
			canvas.height = initCanvasDim;
			canvases[this._id] = canvas;
			document.body.appendChild(canvas);
		}
  }

  _fragmentFreeSpace (l0: number, r0: number, t0: number, b0: number) {
    const w0 = r0 - l0;
    const h0 = b0 - t0;

    let l1: number;
    let r1: number;
    let t1: number;
    let b1: number;
    
    let freeBox: ElementBounds;
    const newFreeSpaceBoxes: Array<ElementBounds> = [];
    let freeSpacePtr = this._freeSpace.first;
    while (freeSpacePtr !== null) {
      freeBox = freeSpacePtr.object;

      l1 = freeBox.left;
      r1 = freeBox.right;
      t1 = freeBox.top;
      b1 = freeBox.bottom;

      const l2 = (l0 < l1) ? l0 : l1;
      const r2 = (r0 > r1) ? r0 : r1;
      const t2 = (t0 < t1) ? t0 : t1;
      const b2 = (b0 > b1) ? b0 : b1;
      if ((r2 - l2 < w0 + freeBox.width) && (b2 - t2 < h0 + freeBox.height)) {
        // the free space box intersect with the newly created box

        // saving ptr to the next element
        const next = freeSpacePtr.next;

        // removing box from free space
        this._freeSpace.remove(freeBox.element);

        // creating new free space boxes

        // box 1
        if (l0 - l1 > 0 && b1 - t1 > 0) {
          newFreeSpaceBoxes.push(new ElementBounds(l1, l0, t1, b1));
        }

        // box 2
        if (r1 - l1 > 0 && t0 - t1 > 0) {
          newFreeSpaceBoxes.push(new ElementBounds(l1, r1, t1, t0));
        }

        // box 3
        if (r1 - r0 > 0 && b1 - t1 > 0) {
          newFreeSpaceBoxes.push(new ElementBounds(r0, r1, t1, b1));
        }

        // box 4
        if (r1 - l1 > 0 && b1 - b0 > 0) {
          newFreeSpaceBoxes.push(new ElementBounds(l1, r1, b0, b1));
        }

        freeSpacePtr = next;
      } else {
        freeSpacePtr = freeSpacePtr.next;
      }

    }

    // Inserting the newly created free space boxes
    this._addToFreeSpace(newFreeSpaceBoxes);
  }

  add (width: number, height: number) {
    // Finding placement for element such that the total area of occupied space remains minimum
    let minArea = Infinity;
    let minBox: Bounds | null = null;
    const position = { x: 0, y: 0 }

    // Iterating through the free space
    let freeSpacePtr = this._freeSpace.first;
    while (freeSpacePtr !== null) {
      const freeBox = freeSpacePtr.object as Bounds;

      // Checking for inclusion
      if (width <= freeBox.width && height <= freeBox.height) {

        // Checking for new total area if inserted at position
        const x = freeBox.left;
        const y = freeBox.top;

        const areaW = (x + width > this.priorityZone.right) ? x + width : this.priorityZone.right;
        const areaH = (y + height > this.priorityZone.bottom) ? y + height : this.priorityZone.bottom;
        const area = areaW * areaH;
        if (minBox === null || area < minArea || (area === minArea && freeBox.area < minBox.area)) {
          position.x = x;
          position.y = y;

          minArea = area;
          minBox  = freeBox;
        }
      }
      freeSpacePtr = freeSpacePtr.next;
    }

    if (minBox === null) {
      return null;
    }

    const left = position.x;
    const right = position.x + width;
    const top = position.y;
    const bottom = position.y + height;

    this._fragmentFreeSpace(left, right, top, bottom);

    // if (left < this.priorityZone.left) { this.priorityZone.left = left; }
    // if (right > this.priorityZone.right) { this.priorityZone.right = right; }
    // if (top < this.priorityZone.top) { this.priorityZone.top = top; }
    // if (bottom > this.priorityZone.bottom) { this.priorityZone.bottom = bottom; }

    if (left < this.occupiedBounds.left) { this.occupiedBounds.left = left; }
    if (right > this.occupiedBounds.right) { this.occupiedBounds.right = right; }
    if (top < this.occupiedBounds.top) { this.occupiedBounds.top = top; }
    if (bottom > this.occupiedBounds.bottom) { this.occupiedBounds.bottom = bottom; }

    if (debug) {
      this._renderDebug();
    }

    return new Bounds(left, right, top, bottom);
  }

  private _addToFreeSpace (freeSpaceBoxes: ElementBounds[]) {
    for (var i = 0; i < freeSpaceBoxes.length; i += 1) {
      const newFreeBox = freeSpaceBoxes[i];
      const l = newFreeBox.left;
      const r = newFreeBox.right;
      const t = newFreeBox.top;
      const b = newFreeBox.bottom;

      let insert = true;

      // Checking for inclusion in existing free space
      let freeSpacePtr = this._freeSpace.first;
      while (freeSpacePtr !== null) {
        const freeBox = freeSpacePtr.object;
        freeSpacePtr = freeSpacePtr.next;

        if (freeBox.left <= l && r <= freeBox.right && freeBox.top <= t && b <= freeBox.bottom) {
          insert = false;
          break;
        }
      }

      if (insert === false) {
        continue;
      }

      // Checking for inclusion in other newly created free space boxes
      for (var j = i + 1; j < freeSpaceBoxes.length; j += 1) {
        const freeBox = freeSpaceBoxes[j];
        if (freeBox.left <= l && r <= freeBox.right && freeBox.top <= t && b <= freeBox.bottom) {
          insert = false;
          break;
        }
      }

      if (insert === true) {
        // free box is not included in any other box of the free space
        newFreeBox.element = this._freeSpace.add(newFreeBox);
      }
    }
  }

  free (area: Bounds) {
    const freeSpaceBox = new ElementBounds(
      area.left,
      area.right,
      area.top,
      area.bottom,
    );

    this._addToFreeSpace([freeSpaceBox]);

    // TODO: merge free spaces when possible
    // idea:
    // - make a stack of free spaces to visit
    // - start with the newly added free space
    // - for every adjecent free space either extend
    //   or merge
    //   or create a new extended space by merging subsets of the 2 free spaces
    // - add this free space to the stack
    // - keep track of every free space created or extended
    // - check for inclusion of every free space tracked with any other tracked freed space

    const newBoxes = [freeSpaceBox];
    for (var i = 0; i < newBoxes.length; i += 1) {
      const newBox = newBoxes[i];
      const left = newBox.left;
      const right = newBox.right;
      const top = newBox.top;
      const bottom = newBox.bottom;

      // Checking for inclusion in existing free space
      let freeSpacePtr = this._freeSpace.first;
      while (freeSpacePtr !== null) {
        const otherBox = freeSpacePtr.object;
        const otherLeft = otherBox.left;
        const otherRight = otherBox.right;
        const otherTop = otherBox.top;
        const otherBottom = otherBox.bottom;

        const currentPtr = freeSpacePtr;
        freeSpacePtr = freeSpacePtr.next;

        const il = otherLeft - left;
        const ir = otherRight - right;

        const it = otherTop - top;
        const ib = otherBottom - bottom;

        const inclusionX = il * ir <= 0;
        const inclusionY = it * ib <= 0;

        const overlapX = (otherLeft - right) * (otherRight - left) <= 0;
        const overlapY = (otherTop - bottom) * (otherBottom - top) <= 0;

        if (!overlapX && !overlapY) {
          // nothing to see here
          continue;
        }

        if (inclusionX) {
          if (!overlapY) {
            continue;
          }

          if (inclusionY) {
            // One box inside the other!
            if (otherLeft <= left && right <= otherRight && otherTop <= top && bottom <= otherBottom) {
              if (otherBox === newBox) {
                // the box just found itself
                continue;
              }
              // console.error('WARNING - I dont think it can happen, can it?')
            } else if (left <= otherLeft && otherRight <= right && top <= otherTop && otherBottom <= bottom) {
              // removing included box
              // console.error('REMOVING - included box', newBox.toString(), 'includes', otherBox.toString());
              this._freeSpace.remove(currentPtr);
            }
          } else {
            // Expanding one of the boxes
            if (otherLeft <= left && right <= otherRight) {
              // console.error('EXPANDING - new box, X inclusion', newBox.toString(), 'in', otherBox.toString());
              newBox.top = Math.min(top, otherTop);
              newBox.bottom = Math.max(bottom, otherBottom);
              newBoxes.push(newBox);
              // console.error(' --> after expansion', newBox.toString());
              break;
            } else {
              // console.error('EXPANDING - other box, X inclusion', newBox.toString(), 'includes', otherBox.toString());
              otherBox.top = Math.min(top, otherTop);
              otherBox.bottom = Math.max(bottom, otherBottom);
              newBoxes.push(otherBox);
              // console.error(' --> after expansion', otherBox.toString());
            }
          }
        } else if (inclusionY) {
          if (!overlapX) {
            continue;
          }

          // Expanding one of the boxes
          if (otherTop <= top && bottom <= otherBottom) {
            // console.error('EXPANDING - new box, Y inclusion', newBox.toString(), 'in', otherBox.toString());
            newBox.left = Math.min(left, otherLeft);
            newBox.right = Math.max(right, otherRight);
            newBoxes.push(newBox);
            // console.error(' --> after expansion', newBox.toString());
            break;
          } else {
            // console.error('EXPANDING - other box, Y inclusion', newBox.toString(), 'includes', otherBox.toString());
            otherBox.left = Math.min(left, otherLeft);
            otherBox.right = Math.max(right, otherRight);
            newBoxes.push(otherBox);
            // console.error(' --> after expansion', otherBox.toString());
          }
        }
      }
    }

    // TODO: update occupied bounds and priority zone

    if (debug) {
      this._renderDebug();
    }
  }

  private _renderDebug () {
    const canvas = canvases[this._id];
    const context = canvas.getContext('2d');
    
    // First pass to get dimensions
    let width = canvas.width;
    let height = canvas.height;
    let freeSpacePtr = this._freeSpace.first;
    while (freeSpacePtr !== null) {
      const box = freeSpacePtr.object;
      if (isFinite(box.bottom) && box.bottom > height) { height = box.bottom; }
      if (isFinite(box.right) && box.right > width) { width = box.right; }
      freeSpacePtr = freeSpacePtr.next;
    }

    if (width > canvas.width || height > canvas.height) {
      canvas.width = width;
      canvas.height = height;
      canvas.style.width = (width / 2) + 'px';
      canvas.style.height = (height / 2) + 'px';
    } else {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }

    freeSpacePtr = this._freeSpace.first;
    while (freeSpacePtr !== null) {
      const box = freeSpacePtr.object;

      // Second pass to render free spaces
      const left = box.left;
      const top = box.top;
      const width = box.width;
      const height = box.height;
      context.fillStyle = 'rgba(255, 0, 0, 0.1)';
      context.fillRect(left, top, width, height);
      context.strokeStyle = 'rgba(255, 0, 0, 1.0)';
      context.strokeRect(left, top, width, height);

      freeSpacePtr = freeSpacePtr.next;
    }
  }

  // displayFreeSpace () {
  //   console.log('Free Space:', this._freeSpace.count, ' boxes');
  //   var freeSpacePtr = this._freeSpace.first;
  //   while (freeSpacePtr !== null) {
  //     var box = freeSpacePtr.object;
  //     console.log('\t[' + box.left + ',' + box.right + '] X [' + box.top + ',' + box.bottom + ']');
  //     freeSpacePtr = freeSpacePtr.next;
  //   }
  // }
}
